import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import "react-responsive-carousel/lib/styles/carousel.min.css";

import {
  faCar,
  faCarSide,
  faCog,
  faGasPump,
  faTachometerAlt,
  faUser,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Carousel } from "react-responsive-carousel";
import { FaIdBadge, FaPhone } from "react-icons/fa";
import Modal from "react-modal";
import api from "../../api/http";

import ProductComments from "./Comments";
import AddComment from "./AddComments";
import Rating from "./Rating";
import ListRating from "./List_Rating";
import NormalizedCarImage from "./NormalizedCarImage";

import "../../styles/produs/CarDetails.css";

Modal.setAppElement("#root");

const CarDetail = () => {
  const { id } = useParams();

  const [car, setCar] = useState(null);
  const [totalRating, setTotalRating] = useState(0);
  const [totalVotes, setTotalVotes] = useState(0);

  const [formData, setFormData] = useState({
    prenume: "",
    virsta: "",
    phone: "",
    fromDate: "",
    fromTime: "",
    toDate: "",
    toTime: "",
  });

  const [messageSent, setMessageSent] = useState(false);
  const [error, setError] = useState("");
  const [totalDays, setTotalDays] = useState(0);

  const intervalRef = useRef(null);
  const successTimeoutRef = useRef(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [commentsRefreshKey, setCommentsRefreshKey] = useState(0);

  const stopInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const fetchCarData = useCallback(async () => {
    try {
      const res = await api.get(`/produs/car/${id}`);
      const data = res.data;
      setCar(data);
      setTotalRating(data?.total_rating ?? 0);
    } catch (e) {
      console.error("Error fetching car details:", e);
    }
  }, [id]);

  const startInterval = useCallback(() => {
    stopInterval();
    intervalRef.current = setInterval(() => {
      fetchCarData();
    }, 5000);
  }, [fetchCarData, stopInterval]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [id]);

  useEffect(() => {
    const onFocus = () => startInterval();
    const onBlur = () => stopInterval();
    window.addEventListener("focus", onFocus);
    window.addEventListener("blur", onBlur);
    fetchCarData();
    startInterval();
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("blur", onBlur);
      stopInterval();
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    };
  }, [fetchCarData, startInterval, stopInterval]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleAgeChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 2);
    setFormData((p) => ({ ...p, virsta: value }));
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/[^\d+]/g, "");
    setFormData((p) => ({ ...p, phone: value }));
  };

  useEffect(() => {
    if (formData.fromDate && formData.toDate) {
      const from = new Date(formData.fromDate).getTime();
      const to = new Date(formData.toDate).getTime();
      setTotalDays(Math.ceil((to - from) / (1000 * 3600 * 24)));
    } else {
      setTotalDays(0);
    }
  }, [formData.fromDate, formData.toDate]);

  const isNightTime = (hour, day) => {
    if (day === 0) return true;
    if (day === 6) return hour >= 16 || hour < 8;
    return hour >= 18 || hour < 8;
  };

  const calculatePrice = () => {
    if (!car) return 0;
    if (totalDays >= 1 && totalDays <= 2) return car.price1;
    if (totalDays >= 3 && totalDays <= 7) return car.price2;
    if (totalDays >= 8 && totalDays <= 20) return car.price3;
    if (totalDays >= 21 && totalDays <= 45) return car.price4;
    if (totalDays >= 46) return car.price5;
    return 0;
  };

  const calculateFinalPrice = () => {
    const fromDate = new Date(`${formData.fromDate}T${formData.fromTime || "00:00"}`);
    const toDate = new Date(`${formData.toDate}T${formData.toTime || "00:00"}`);
    const nightTimeTax = 10;
    const fromNightTax = isNightTime(fromDate.getHours(), fromDate.getDay()) ? nightTimeTax : 0;
    const toNightTax = isNightTime(toDate.getHours(), toDate.getDay()) ? nightTimeTax : 0;
    const dailyPrice = calculatePrice();
    const finalPrice = dailyPrice * totalDays + fromNightTax + toNightTax;
    return { finalPrice, fromNightTax, toNightTax };
  };

  const parseReservationError = async (err) => {
    const data = err?.response?.data;
    if (data?.error) return data.error;
    if (typeof data === "string") return data;
    if (err?.message) return err.message;
    return "Eroare la trimiterea mesajului";
  };

  const sendRezervation = async () => {
    setError("");
    setMessageSent(false);
    const required = [formData.fromDate, formData.fromTime, formData.toDate, formData.toTime, formData.prenume, formData.virsta, formData.phone];
    if (required.some((v) => !v || String(v).trim() === "")) {
      setError("Te rog completează toate câmpurile.");
      return;
    }
    const age = Number(formData.virsta);
    if (!/^\d{2}$/.test(formData.virsta)) { setError("Vârsta nu poate fi mai mică decât 18 ani."); return; }
    if (age < 18 || age > 99) { setError("Vârsta nu poate fi mai mică decât 18."); return; }
    if (!car) { setError("Datele mașinii nu sunt încă încărcate."); return; }
    const final = calculateFinalPrice();
    const payload = {
      ...formData,
      virsta: Number(formData.virsta),
      carInfo: { id: car.id, name: car.name, producator: car.producator, gaj: car.gaj },
      totalDays,
      priceForTotalDays: calculatePrice(),
      Pret_final: final.finalPrice,
      noapte_preluare: final.fromNightTax,
      noapte_returnare: final.toNightTax,
    };
    try {
      const res = await api.post("/produs/reservation-email", payload);
      if (res?.data?.success === true || res.status === 200) {
        setMessageSent(true);
        if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = setTimeout(() => { setMessageSent(false); successTimeoutRef.current = null; }, 8000);
        setFormData({ prenume: "", virsta: "", phone: "", fromDate: "", fromTime: "", toDate: "", toTime: "" });
      } else {
        setError(res?.data?.error || "Eroare la trimiterea mesajului");
      }
    } catch (err) {
      setError(await parseReservationError(err));
    }
  };

  const openModal = (index) => { setCurrentSlide(index); setIsModalOpen(true); };
  const closeModal = () => setIsModalOpen(false);

  const motorLabel = (v) => ["Diesel","Hybrid","Petrol","Electric","Petrol-Hybrid","Petrol-Gaz"][v] ?? "—";
  const cutiaLabel = (v) => v === 0 ? "Manual" : v === 1 ? "Automat" : "—";
  const usiLabel = (v) => v === 0 ? "3" : v === 1 ? "5" : "—";
  const pasageriLabel = (v) => ["2","4","5","7"][v] ?? "0";
  const caroserieLabel = (v) => ["Van","Universal","Minivan","Roadster","SUV","Cabriolet","Microvan","Pickup","Sedan","Crossover","Hatchback","Combi","Coupe"][v] ?? "—";

  const { finalPrice, fromNightTax, toNightTax } = calculateFinalPrice();

  if (!car) return (
      <div className="cd-loading">
        <div className="cd-spinner" />
        <span>Se încarcă...</span>
      </div>
  );

  return (
      <div className="cd-page">
        <div className="cd-hero">
          <div className="cd-hero-inner">
            <div className="cd-hero-gallery">
              <div className="cd-badge-year">{car.an}</div>
              <Carousel
                  showStatus={false}
                  showThumbs={true}
                  infiniteLoop={true}
                  selectedItem={currentSlide}
                  onClickItem={(i) => openModal(i)}
                  className="cd-carousel"
              >
                {car?.images?.map((img, i) => (
                    <div key={i} className="cd-slide">
                      <NormalizedCarImage
                        src={img.image}
                        alt={`${car.name} #${i + 1}`}
                        variant="details"
                      />
                      <div className="cd-slide-overlay" />
                    </div>
                ))}
              </Carousel>
            </div>

            <div className="cd-hero-info">
              <p className="cd-label-brand">{car.producator}</p>
              <h1 className="cd-title">{car.name}</h1>
              <div className="cd-price-strip">
                <p className="cd-price-strip-title">Tarife zilnice</p>
                <div className="cd-price-strip-grid">
                  {[
                    { label: "1–2 zile", val: car.price1 },
                    { label: "3–7 zile", val: car.price2 },
                    { label: "8–20 zile", val: car.price3 },
                    { label: "21–45 zile", val: car.price4 },
                    { label: "46+ zile", val: car.price5 },
                  ].map(({ label, val }) => (
                      <div className="cd-price-cell" key={label}>
                        <span className="cd-price-val">{val} €</span>
                        <span className="cd-price-lbl">{label}</span>
                      </div>
                  ))}
                </div>
              </div>

              <div className="cd-specs-pills">
                <span className="cd-pill"><FontAwesomeIcon icon={faCog} />{cutiaLabel(car.cutia)}</span>
                <span className="cd-pill"><FontAwesomeIcon icon={faGasPump} />{motorLabel(car.motor)}</span>
                <span className="cd-pill"><FontAwesomeIcon icon={faCarSide} />{caroserieLabel(car.caroserie)}</span>
                <span className="cd-pill"><FontAwesomeIcon icon={faUsers} />{pasageriLabel(car.numar_pasageri)} locuri</span>
                <span className="cd-pill"><FontAwesomeIcon icon={faTachometerAlt} />{car.km_limit}</span>
                <span className="cd-pill"><FontAwesomeIcon icon={faCar} />{usiLabel(car.numar_usi)} uși</span>
              </div>

            </div>
          </div>
        </div>

        <div className="cd-body">
          <p className="cd-description">{car.descriere}</p>
          <section className="cd-section cd-rezervare">
            <div className="cd-section-header">
              <span className="cd-section-number">01</span>
              <h2 className="cd-section-title">Rezervare</h2>
            </div>

            <div className="cd-rezv-grid">
              <div className="cd-rezv-dates">
                <p className="cd-field-label">Preluare</p>
                <div className="cd-date-row">
                  <input type="date" name="fromDate" className="cd-input" value={formData.fromDate} onChange={handleChange}
                         min={new Date().toISOString().split("T")[0]} placeholder="Data" />
                  <input type="time" name="fromTime" className="cd-input cd-input-time" value={formData.fromTime} onChange={handleChange} />
                </div>
                <p className="cd-field-label" style={{ marginTop: 16 }}>Returnare</p>
                <div className="cd-date-row">
                  <input type="date" name="toDate" className="cd-input" value={formData.toDate} onChange={handleChange}
                         min={formData.fromDate || new Date().toISOString().split("T")[0]} />
                  <input type="time" name="toTime" className="cd-input cd-input-time" value={formData.toTime} onChange={handleChange} />
                </div>

                <div className="cd-fields-row">
                  <div className="cd-field-wrap">
                    <FontAwesomeIcon icon={faUser} className="cd-field-icon" />
                    <input type="text" name="prenume" className="cd-input cd-input-icon" placeholder="Nume / Prenume"
                           value={formData.prenume} onChange={handleChange} />
                  </div>
                  <div className="cd-field-wrap">
                    <FaIdBadge className="cd-field-icon" />
                    <input type="text" name="virsta" className="cd-input cd-input-icon" placeholder="Vârsta șofer"
                           value={formData.virsta} maxLength={2} onChange={handleAgeChange} />
                    {formData.virsta && <span className="cd-age-suffix">ani</span>}
                  </div>
                  <div className="cd-field-wrap">
                    <FaPhone className="cd-field-icon" />
                    <input type="tel" name="phone" className="cd-input cd-input-icon" placeholder="Număr de telefon"
                           value={formData.phone} onChange={handlePhoneChange} />
                  </div>
                </div>
              </div>

              <div className="cd-rezv-summary">
                <p className="cd-summ-title">Sumar cost</p>
                <div className="cd-summ-rows">
                  <div className="cd-summ-row"><span>Gaj</span><span>{car.gaj} €</span></div>
                  <div className="cd-summ-row"><span>Preț / zi</span><span>{calculatePrice()} €</span></div>
                  <div className="cd-summ-row"><span>Număr zile</span><span>× {totalDays}</span></div>
                  <div className="cd-summ-row"><span>Taxa noapte preluare</span><span>{fromNightTax} €</span></div>
                  <div className="cd-summ-row"><span>Taxa noapte returnare</span><span>{toNightTax} €</span></div>
                  <div className="cd-summ-divider" />
                  <div className="cd-summ-row cd-summ-total">
                    <span>Total</span>
                    <span className="cd-summ-total-val">{finalPrice} €</span>
                  </div>
                </div>

                {error && <p className="cd-error">{error}</p>}
                {messageSent && <p className="cd-success">✓ Mesaj trimis! Vă vom contacta în scurt timp.</p>}

                <button className="cd-btn-send" onClick={sendRezervation}>
                  Trimite rezervarea
                  <span className="cd-btn-arrow">→</span>
                </button>
              </div>
            </div>
          </section>

          <section className="cd-section cd-rating-section">
            <div className="cd-section-header">
              <span className="cd-section-number">02</span>
              <h2 className="cd-section-title">Recenzii</h2>
              <div className="cd-rating-badge">
                <span className="cd-rating-num">{totalRating}</span>
                <span className="cd-rating-sep">/</span>
                <span className="cd-rating-votes">{totalVotes}</span>
              </div>
            </div>

            <div className="cd-rating-body">
              <div className="cd-rating-left">
                <Rating productId={id} onRated={() => fetchCarData()} />
              </div>
              <div className="cd-rating-right">
                <ListRating productId={id} onUpdateTotalVotes={(v) => setTotalVotes(v)} />
              </div>
            </div>
          </section>

          <section className="cd-section cd-comments-section">
            <div className="cd-section-header">
              <span className="cd-section-number">03</span>
              <h2 className="cd-section-title">Comentarii</h2>
            </div>
            <AddComment productId={id} onSuccess={() => setCommentsRefreshKey((k) => k + 1)} />
            <ProductComments key={commentsRefreshKey} productId={id} />
          </section>
        </div>

        <Modal
            isOpen={isModalOpen}
            onRequestClose={closeModal}
            shouldCloseOnOverlayClick={true}
            shouldCloseOnEsc={true}
            contentLabel="Image Carousel Modal"
            className="cd-modal"
            overlayClassName="cd-overlay"
        >
          <button onClick={closeModal} className="cd-modal-close">✕</button>
          <Carousel
              showStatus={false}
              showThumbs={false}
              showArrows={true}
              infiniteLoop={true}
              selectedItem={currentSlide}
              onChange={setCurrentSlide}
              useKeyboardArrows={true}
              swipeable={true}
              emulateTouch={true}
          >
            {car?.images?.map((img, i) => (
                <div key={i} onClick={(e) => e.stopPropagation()}>
                  <NormalizedCarImage
                    src={img.image}
                    alt={`${car.name} #${i + 1}`}
                    variant="modal"
                  />
                </div>
            ))}
          </Carousel>
        </Modal>
      </div>
  );
};

export default CarDetail;